import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReduxState } from "../../../../redux";
import { projectAiChatSendMessage } from "../../../../docuclipper/api";
import { FeatureGateByPlan } from "../../../Docuclipper/FeatureGate/FeatureGateByPlan";

import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const WELCOME_MESSAGE: Message = {
  role: "assistant",
  content:
    "Hi! I can answer questions about the transactions in this project. Try asking: 'What are my top expense categories?' or 'Show me transactions over $1000'.",
};

function generateSessionId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

const ChatContent: React.FC = () => {
  const jobId = useSelector((state: ReduxState) => state.JobData.job?.id);

  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const sessionId = useRef<string>(generateSessionId());
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg: Message = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await projectAiChatSendMessage({
        jobId: jobId as string | number,
        sessionId: sessionId.current,
        message: trimmed,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.content },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div style={{ overflowY: "auto", flex: 1, padding: "16px" }}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            style={{
              textAlign: msg.role === "user" ? "right" : "left",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                display: "inline-block",
                backgroundColor: msg.role === "user" ? "#e9ecef" : "#fff",
                border: "1px solid #dee2e6",
                borderRadius: "10px",
                padding: "8px 12px",
                maxWidth: "85%",
                wordBreak: "break-word",
                textAlign: "left",
                fontSize: "14px",
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ textAlign: "left", marginBottom: "8px" }}>
            <Spinner animation="border" size="sm" />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ padding: "12px 16px", borderTop: "1px solid #dee2e6" }}>
        <Form onSubmit={handleSubmit} className="d-flex" style={{ gap: "8px" }}>
          <Form.Control
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            placeholder="Ask a question — e.g. 'What are the largest expenses?' or 'Are there any unusual transactions?'"
            style={{ fontSize: "14px" }}
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={loading || !input.trim()}
          >
            <FontAwesomeIcon icon="paper-plane" />
          </Button>
        </Form>
      </div>
    </div>
  );
};

interface AiChatDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const AiChatDrawer: React.FC<AiChatDrawerProps> = ({ open, onClose }) => {
  const history = useHistory();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="400px"
      zIndex={10000}
      style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px",
            borderBottom: "1px solid #dee2e6",
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: "15px" }}>
            <FontAwesomeIcon icon="robot" className="mr-2" />
            AI Assistant
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
              color: "#6c757d",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div style={{ flex: 1, overflow: "hidden" }}>
          <FeatureGateByPlan
            featureId={5}
            renderEnabled={() => <ChatContent />}
            renderNotEnabled={() => (
              <div style={{ padding: "24px" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>AI Assistant</div>
                <div style={{ color: "#6c757d", marginBottom: 16, fontSize: 14 }}>
                  Available on Business and Enterprise plans.
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    onClose();
                    history.push("/account/upgrade");
                  }}
                >
                  Upgrade to Business
                </Button>
              </div>
            )}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default AiChatDrawer;
